import React, { createContext, useContext, useState, useEffect } from 'react';
import { getData, postData } from '../../src/webService/webservice.js';
import { useStore } from '../Context/context.jsx';
import { toast } from 'react-toastify';

const SubscriptionRenewalContext = createContext();

export const useSubscriptionRenewal = () => useContext(SubscriptionRenewalContext);

export const SubscriptionRenewalProvider = ({ children }) => {
    const { selectedStoreId } = useStore();
    const role = localStorage.getItem("role");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Modal State
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [activePackages, setActivePackages] = useState([]);
    const [selectedRenewPackageId, setSelectedRenewPackageId] = useState('');
    const [renewing, setRenewing] = useState(false);
    const [renewStoreId, setRenewStoreId] = useState(null);

    // Call this function when the user wants to renew
    const openRenewPackageModal = async (overrideStoreId = null) => {
        setIsModalOpen(true);
        setIsLoading(true);
        setSelectedRenewPackageId(''); // Reset temporary state
        setRenewStoreId(overrideStoreId);

        try {
            await fetchCurrentSubscription(overrideStoreId);
            await fetchActivePackages(overrideStoreId);
        } catch (error) {
            console.error("Failed to load renewal data:", error);
            toast.error("Failed to load necessary data for renewal");
            setIsModalOpen(false); // Close if fails to load
        } finally {
            setIsLoading(false);
        }
    };

    const closeRenewPackageModal = () => {
        setIsModalOpen(false);
        // We do not clear packages to allow reuse, but we clear selected state
        setSelectedRenewPackageId('');
        setRenewStoreId(null);
    };

    const isPublicAuthPage = () => {
        const pathname = window.location.pathname;
        if (pathname === '/' || pathname === '') return true;
        const publicPaths = [
            '/login',
            '/signup',
            '/reset-password',
            '/forgot-password',
            '/privacy-policy',
            '/terms-and-conditions'
        ];
        return publicPaths.some(path => pathname.startsWith(path));
    };

    const fetchCurrentSubscription = async (overrideStoreId) => {
        const token = localStorage.getItem("Token");
        const role = localStorage.getItem("role");

        if (!token || !role || isPublicAuthPage()) {
            return null;
        }


        const effectiveSid = overrideStoreId || selectedStoreId || localStorage.getItem('currentStoreId');
        if (!effectiveSid) return null;

        let endpoint = 'store-subscriptions/my-subscription';

        if (['SuperAdmin', 'SelfAdmin'].includes(role) && effectiveSid && effectiveSid !== 'all') {
            endpoint = `store-subscriptions/store/${effectiveSid}`;
        }

        try {
            const res = await getData(endpoint, {}, token);
            if (res?.success && res?.data) {
                setSubscriptionData(res.data);
                return res.data;
            } else if (res?.status === 200 && res?.data) {
                setSubscriptionData(res.data);
                return res.data;
            } else {
                return null;
            }
        } catch (e) {
            console.error("Unable to fetch current subscription", e);
            return null;
        }
    };

    const fetchActivePackages = async (overrideStoreId) => {
        const token = localStorage.getItem("Token");
        const role = localStorage.getItem("role");
        if (!token || !role || isPublicAuthPage()) return [];

        const effectiveSid = overrideStoreId || selectedStoreId || localStorage.getItem('currentStoreId');

        let params = { status: 'Active', limit: 'all' };
        if (effectiveSid && effectiveSid !== 'all') {
            params.storeId = effectiveSid;
        }

        try {
            const res = await getData('packages', params, token);
            let pkgList = [];
            if ((res?.success || res?.status === 200) && Array.isArray(res?.data?.packages)) {
                pkgList = res.data.packages;
            } else if (res?.data?.packages && Array.isArray(res.data.packages)) {
                pkgList = res.data.packages;
            } else if (Array.isArray(res?.packages)) {
                pkgList = res.packages;
            } else if (Array.isArray(res?.data)) {
                pkgList = res.data;
            }

            // EXCLUDE Trial Package for renewals
            pkgList = pkgList.filter(pkg => !pkg.packageName.toLowerCase().includes('trial'));

            setActivePackages(pkgList);
            return pkgList;
        } catch (e) {
            console.error("Unable to fetch packages", e);
            return [];
        }
    };

    // Auto-select package when both subscription and packages are loaded
    useEffect(() => {
        if (isModalOpen && activePackages.length > 0 && !selectedRenewPackageId) {
            setSelectedRenewPackageId(subscriptionData?.packageId || activePackages[0]._id);
        }
    }, [isModalOpen, activePackages, subscriptionData, selectedRenewPackageId]);

    // Fetch subscription data automatically so the header ticker and dashboard can use it
    const loadInitialSubscription = async () => {
        try {
            const token = localStorage.getItem("Token");
            const role = localStorage.getItem("role");

            if (!token || !role || isPublicAuthPage()) return;

            if (role !== "StoreAdmin") {
                return;
            }

            await fetchCurrentSubscription();
        } catch (e) {
            console.error("Context failed to fetch initial subscription data", e);
        }
    };

    const clearSubscriptionState = () => {
        setSubscriptionData(null);
        setActivePackages([]);
        setSelectedRenewPackageId('');
        setIsModalOpen(false);
    };

    useEffect(() => {
        const token = localStorage.getItem("Token");
        if (token && !isPublicAuthPage()) {
            loadInitialSubscription();
        } else {
            clearSubscriptionState();
        }
    }, [selectedStoreId, window.location.pathname]);


    const handleRenewSubscription = async (paymentMode = 'CASHFREE', paymentReference = '', transactionId = '', paymentProof = null, onSuccessCallback) => {
        const sid = renewStoreId || subscriptionData?.storeId || selectedStoreId || localStorage.getItem('currentStoreId');
        if (!sid) {
            toast.error("Store ID is missing");
            return;
        }
        if (!selectedRenewPackageId) {
            toast.error("Please select a package to renew");
            return;
        }

        try {
            setRenewing(true);
            const token = localStorage.getItem("Token");

            if (paymentMode === 'CASHFREE') {
                // 1. Create Order
                const res = await postData('payments/orders', {
                    storeId: sid,
                    packageId: selectedRenewPackageId
                }, token);

                if (res?.success && res.data?.paymentSessionId) {
                    // 2. Initialize Cashfree SDK
                    const { load } = await import('@cashfreepayments/cashfree-js');
                    const cashfree = await load({
                        mode: "production" // Changed from sandbox to production
                    });

                    let checkoutOptions = {
                        paymentSessionId: res.data.paymentSessionId,
                        redirectTarget: "_self", // Or "_modal" if you want in-page
                    };

                    cashfree.checkout(checkoutOptions);
                } else {
                    toast.error(res?.message || "Failed to initiate payment");
                    if (res?.message?.toLowerCase().includes("trial package")) {
                        await fetchActivePackages(sid);
                        setSelectedRenewPackageId('');
                    }
                    setRenewing(false);
                }
            } else {
                // Manual Payment Flow
                const formData = new FormData();
                formData.append('storeId', sid);
                formData.append('packageId', selectedRenewPackageId);
                formData.append('paymentMode', paymentMode);
                if (paymentReference) formData.append('paymentReference', paymentReference);
                if (transactionId) formData.append('transactionId', transactionId);
                if (paymentProof) formData.append('paymentProof', paymentProof);

                const res = await postData('payments/manual', formData, token, true);

                if (res?.success) {
                    toast.success(res?.message || "Payment submitted for approval");
                    closeRenewPackageModal();
                    if (onSuccessCallback) onSuccessCallback();
                } else {
                    toast.error(res?.message || "Failed to submit payment");
                }
                setRenewing(false);
            }
        } catch (err) {
            toast.error(err.message || "Error initiating payment");
            setRenewing(false);
        }
    };

    return (
        <SubscriptionRenewalContext.Provider
            value={{
                isModalOpen,
                isLoading,
                subscriptionData,
                activePackages,
                selectedRenewPackageId,
                renewing,
                setSelectedRenewPackageId,
                openRenewPackageModal,
                closeRenewPackageModal,
                handleRenewSubscription,
                fetchCurrentSubscription,
                role
            }}
        >
            {children}
        </SubscriptionRenewalContext.Provider>
    );
};
